import { IFileToUpload } from "../interfaces/IFileToUpload";
import authModule from "../../../utilities/AuthModule";
import { getFilePreCommitInfo, getFolderPreCommitInfo } from "./SharePointRestServices";
import { ISiteFileDestinationInfo } from "../interfaces/ISiteFileDestinationInfo";
import { logger } from "../../../utilities/logger";
import { IFilePreCommitResult } from "../interfaces/IFilePreCommitResult";
import { FolderPreCommitRequest } from "../components/_v2/redux/api/local/api-local-interfaces";
import { DragAndDropHelper } from "../../../utilities/DragAndDropHelper";
import { SaveMode } from "../components/enums/SaveModeEnum";

export class FileService {
  constructor() { }

  static getFilePreCommitCheck = async (files: IFileToUpload[], siteFileDestinationInfo: ISiteFileDestinationInfo, convertEmailToPDF: boolean, saveMode?: SaveMode): Promise<IFileToUpload[]> => {
    const accessToken = await authModule.getOneDriveAccessToken()
    return await Promise.all(files.map(async item => {
      let result: IFilePreCommitResult;

      if (item.isSelected) {
        if (item.isFolder && !item.parentFolderId) {
          const folderRequest: FolderPreCommitRequest = {
            accessToken,
            destinationUrl: siteFileDestinationInfo.destinationUrl,
            siteUrl: siteFileDestinationInfo.siteUrl,
            folderName: item.name
          }

          result = await getFolderPreCommitInfo(folderRequest);
        }
        //root file
        else if (!item.isFolder && !item.parentFolderId) {
          if ((DragAndDropHelper.isDroppedModeActive && item.isFileSystemEntry) || (!DragAndDropHelper.isDroppedModeActive && !item.isFileSystemEntry)) {
            result = await getFilePreCommitInfo({
              accessToken,
              autoFolderCreation: siteFileDestinationInfo.autoFolderCreation,
              fileName: (item.isMainMailItem && convertEmailToPDF) || (saveMode === SaveMode.SaveAsPdf && item.canSaveAsPDF) ? `${item.name}.pdf` : `${item.name}.${item.extension}`,
              destinationUrl: siteFileDestinationInfo.destinationUrl,
              siteUrl: siteFileDestinationInfo.siteUrl
            })
          } else {
            result = { exists: false, checkedOut: false, checkedOutBy: null, locked: false, lockedBy: null }
          }
        } else {
          result = { exists: false, checkedOut: false, checkedOutBy: null, locked: false, lockedBy: null }
        }
      } else {
        result = { exists: false, checkedOut: false, checkedOutBy: null, locked: false, lockedBy: null };
      }

      return {
        ...item,
        isDuplicate: result.exists,
        filePreCommitResult: result
      };
    }));
  };

  static GetFileContent = async (): Promise<string> => {
    try {
      const result = await new Promise<string>((resolve, reject) => {
        // Restrict iPad to 64k
        const chunkSize = (Office.context.touchEnabled === true && Office.context.commerceAllowed === false) ? 65536 : 4194304;

        Office.context.document.getFileAsync(Office.FileType.Compressed, { sliceSize: chunkSize }, async (asyncResult) => {
          if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
            try {
              const fileData = await FileService.getAllSlices(asyncResult.value);
              const fileBase64Content: string = FileService.onGotAllSlices(fileData);
              resolve(fileBase64Content);
            } catch (error) {
              reject(error);
            }
          } else {
            reject(asyncResult.error);
          }
        });
      });

      if (result && result?.length > 0) {
        return result;
      }
    } catch (error) {
      logger(error, "FileService.ts (GetFileContent)");
    }

    return null;
  };

  private static getAllSlices(file: Office.File): Promise<any> {
    return new Promise(async (resolve, reject) => {
      let documentFileData = [];
      for (let sliceIndex = 0; sliceIndex < file.sliceCount; sliceIndex++) {
        const sliceReadPromise = new Promise((sliceResolve, sliceReject) => {
          file.getSliceAsync(sliceIndex, (asyncResult) => {
            if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
              documentFileData = documentFileData.concat(asyncResult.value.data);
              sliceResolve(null);
            } else {
              file.closeAsync();
              sliceReject(`Error in reading slice no: ${sliceIndex} of the document`);
            }
          });
        });

        try {
          await sliceReadPromise;
        } catch (error) {
          return reject(error);
        }
      }

      if (!documentFileData.length) {
        return reject("Error while reading document. Please try it again.");
      }

      file.closeAsync();

      resolve(documentFileData);
    });
  };

  private static onGotAllSlices = (docdataSlices: any[]): string => {
    let fileContent = "";
    for (let j = 0; j < docdataSlices.length; j++) {
      fileContent += String.fromCharCode(docdataSlices[j]);
    }
    const encodedData = btoa(fileContent);
    return encodedData;
  };
}
